// Изгледът на „Продажби по периоди" — режимът (групирано / във времето) и
// единицата (часове, дни, месеци…) — запомнен в localStorage на браузъра:
// веднъж настроена, справката се отваря така и следващия път.
//
// Запомнено е лично за браузъра, не за ресторанта — затова localStorage, а не
// настройка на BE. Непозната или невалидна стойност (стар запис, ръчно
// пипане) тихо връща подразбирането.
//
// „Натовареност" на резервациите ползва същия изглед със СВОЙ ключ — двете
// справки се помнят поотделно.

import { useEffect, useState } from "react";

import {
  SALES_BY_PERIODS_UNITS,
  type SalesByPeriodsMode,
  type SalesByPeriodsUnit,
} from "../../../../api/reportsSalesByPeriods";

const STORAGE_KEY = "reports.salesByPeriods.view.v1";

interface SalesByPeriodsView {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
}

const DEFAULT_VIEW: SalesByPeriodsView = { mode: "grouped", unit: "hour" };

const isMode = (value: unknown): value is SalesByPeriodsMode =>
  typeof value === "string" && value in SALES_BY_PERIODS_UNITS;

const readView = (storageKey: string): SalesByPeriodsView => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_VIEW;
    const parsed = JSON.parse(raw) as Partial<SalesByPeriodsView>;
    if (!isMode(parsed?.mode)) return DEFAULT_VIEW;
    const units = SALES_BY_PERIODS_UNITS[parsed.mode];
    const unit = units.find((candidate) => candidate === parsed.unit);
    return { mode: parsed.mode, unit: unit ?? units[0] };
  } catch {
    return DEFAULT_VIEW;
  }
};

export const useSalesByPeriodsView = (storageKey: string = STORAGE_KEY) => {
  const [view, setView] = useState<SalesByPeriodsView>(() => readView(storageKey));

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(view));
    } catch {
      // Пълен или недостъпен localStorage не бива да чупи справката.
    }
  }, [view, storageKey]);

  // Смяната на режима сменя и единиците — „дни от седмицата" няма във
  // „във времето", затова единицата се връща на първата валидна.
  const changeMode = (mode: SalesByPeriodsMode) =>
    setView((previous) => ({
      mode,
      unit: SALES_BY_PERIODS_UNITS[mode].includes(previous.unit)
        ? previous.unit
        : SALES_BY_PERIODS_UNITS[mode][0],
    }));

  const changeUnit = (unit: SalesByPeriodsUnit) =>
    setView((previous) => ({ ...previous, unit }));

  return { mode: view.mode, unit: view.unit, changeMode, changeUnit };
};
